import React from 'react';
import SearchableDropdown from '../common/SearchableDropdown';
import TuitionTypeOptionContent from '../common/TuitionTypeOptionContent';
import { EDUCATION_SYSTEM_OPTIONS, getEducationLevelsForSystem } from '@tutor/types';
import type { ParentRegistrationFormData } from '../../types';
import { useOptionalI18n } from '@/i18n';
import { localizeText } from '@/i18n/options';

interface TuitionDetailsStepProps {
  formData: ParentRegistrationFormData;
  errors: Record<string, string>;
  onChange: (formData: Partial<ParentRegistrationFormData>) => void;
  showBothOption?: boolean;
}

const TuitionDetailsStep: React.FC<TuitionDetailsStepProps> = ({
  formData,
  errors,
  onChange,
  showBothOption = true,
}) => {
  const { locale } = useOptionalI18n();
  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-1 h-5 bg-accent rounded-full" />
        <h3 className="text-lg font-semibold text-gray-900">{localizeText(locale, 'Tuition Details')}</h3>
      </div>
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {localizeText(locale, 'Type of Tuition')} <span className="text-red-500">*</span>
          </label>
          <div className={`grid ${showBothOption ? 'grid-cols-3' : 'grid-cols-2'} gap-4`}>
            <button
              onClick={() => onChange({ tuitionType: 'physical' })}
              className={`flex min-h-[10.5rem] flex-col items-center justify-center gap-3 rounded-xl px-4 py-5 font-medium transition-all cursor-pointer ${
                formData.tuitionType === 'physical'
                  ? 'bg-primary-50 border-2 border-primary text-primary shadow-sm'
                  : errors.tuitionType
                    ? 'border-red-500 hover:border-red-600'
                    : 'bg-white border border-gray-200 text-gray-600 hover:border-primary-200 hover:bg-primary-50/50'
              }`}
            >
              <TuitionTypeOptionContent
                type="physical"
                label={localizeText(locale, 'Physical Tuition')}
              />
            </button>
            <button
              onClick={() => onChange({ tuitionType: 'online' })}
              className={`flex min-h-[10.5rem] flex-col items-center justify-center gap-3 rounded-xl px-4 py-5 font-medium transition-all cursor-pointer ${
                formData.tuitionType === 'online'
                  ? 'bg-primary-50 border-2 border-primary text-primary shadow-sm'
                  : errors.tuitionType
                    ? 'border-red-500 hover:border-red-600'
                    : 'bg-white border border-gray-200 text-gray-600 hover:border-primary-200 hover:bg-primary-50/50'
              }`}
            >
              <TuitionTypeOptionContent
                type="online"
                label={localizeText(locale, 'Online Tuition')}
              />
            </button>
            {showBothOption && (
              <button
                onClick={() => onChange({ tuitionType: 'both' })}
                className={`flex min-h-[10.5rem] flex-col items-center justify-center gap-3 rounded-xl px-4 py-5 font-medium transition-all cursor-pointer ${
                  formData.tuitionType === 'both'
                    ? 'bg-primary-50 border-2 border-primary text-primary shadow-sm'
                    : errors.tuitionType
                      ? 'border-red-500 hover:border-red-600'
                      : 'bg-white border border-gray-200 text-gray-600 hover:border-primary-200 hover:bg-primary-50/50'
                }`}
              >
                <TuitionTypeOptionContent
                  type="both"
                  label={localizeText(locale, 'Both')}
                />
              </button>
            )}
          </div>
          {errors.tuitionType && (
            <p className="text-red-500 text-sm mt-1">{errors.tuitionType}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {localizeText(locale, 'Education System')} <span className="text-red-500">*</span>
          </label>
          <SearchableDropdown
            options={EDUCATION_SYSTEM_OPTIONS}
            value={formData.educationSystem}
            onChange={(value) =>
              onChange({
                educationSystem: String(value),
                level: '',
                subject: '',
              })
            }
            placeholder={localizeText(locale, 'Select Education System')}
            searchable={true}
            className={errors.educationSystem ? 'border-red-500' : ''}
          />
          {errors.educationSystem && (
            <p className="text-red-500 text-sm mt-1">{errors.educationSystem}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {localizeText(locale, 'Education Level')} <span className="text-red-500">*</span>
          </label>
          <SearchableDropdown
            options={formData.educationSystem ? getEducationLevelsForSystem(formData.educationSystem).map(({ id, label }) => ({ value: id, label })) : []}
            value={formData.level}
            onChange={(value) =>
              onChange({
                level: value as ParentRegistrationFormData['level'],
                subject: '',
              })
            }
            placeholder={localizeText(locale, 'Select Level')}
            searchable={true}
            disabled={!formData.educationSystem}
            className={errors.level ? 'border-red-500' : ''}
          />
          {errors.level && (
            <p className="text-red-500 text-sm mt-1">{errors.level}</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default TuitionDetailsStep;
